import { z } from "zod/v4"
import { VoiceReplyId, VoiceReplyIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithLook, Users } from "./user"
import { VoiceDenormalized, VoiceDocuments, VoiceFile } from "./voice"

// ─── VoiceReplyDocument ──────────────────────────────────────────────────────
// Path: users/{userId}/voices/{voiceId}/voice-replies/{voiceReplyId}

export const VoiceReplyMixKind = z.enum(["always", "normal", "ignore"])
  .describe("返信の重ね再生時の挙動。投稿主が制御する。always=常に含める / normal=通常 / ignore=除外")

export type VoiceReplyMixKind = z.infer<typeof VoiceReplyMixKind>

export const VoiceReplyCounts = z.object({
  emote_counts: z.record(z.string(), z.number().int()).describe("この返信の再生中に送られたエモートの種別ごと累積カウント"),
})

export type VoiceReplyCounts = z.infer<typeof VoiceReplyCounts>

export const VoiceReplyDocumentMeta = z.object({
  user: UserDenormalizedWithLook.describe("返信者の非正規化ユーザー情報"),
  file: VoiceFile.describe("返信音声ファイル情報"),
  mix_kind: VoiceReplyMixKind,
  counts: VoiceReplyCounts.describe("集計情報（denormalize）"),
  voice: VoiceDenormalized.describe("元投稿の非正規化情報（通知用）"),
})

export type VoiceReplyDocumentMeta = z.infer<typeof VoiceReplyDocumentMeta>

export const VoiceReplyDocumentBody = z.intersection(
  VoiceReplyDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type VoiceReplyDocumentBody = z.infer<typeof VoiceReplyDocumentBody>

export const VoiceReplyDocument = z.intersection(
  VoiceReplyDocumentBody,
  z.object({
    id: VoiceReplyIdSchema,
  })
)

export type VoiceReplyDocument = z.infer<typeof VoiceReplyDocument>

export class VoiceReplyDocuments {

  static toDocumentData(reply: VoiceReplyDocument): object {
    return {
      ...reply,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): VoiceReplyDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: VoiceReplyId(doc.id),
      user: Users.parseDenormalizedWithLook(data["user"]),
      file: data["file"],
      mix_kind: data["mix_kind"] ?? "normal",
      counts: {
        emote_counts: (data["counts"] ?? {})["emote_counts"] ?? {},
      },
      voice: VoiceDocuments.parseDenormalized(data["voice"]),
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

}

// ─── API Request/Response ────────────────────────────────────────────────────

export const CreateVoiceReplyRequest = z.object({
  audio_location: z.string().describe("アップロード済み返信音声ファイルのStorageパス（gs:// から始まる）"),
  duration_ms: z.number().int().min(1).max(6000).describe("返信音声の長さ（ミリ秒、最大6秒）"),
})
export type CreateVoiceReplyRequest = z.infer<typeof CreateVoiceReplyRequest>

export const CreateVoiceReplyResponse = z.object({
  id: z.string().describe("作成されたボイス返信のID"),
})
export type CreateVoiceReplyResponse = z.infer<typeof CreateVoiceReplyResponse>

export const UpdateVoiceReplyMixKindRequest = z.object({
  mix_kind: VoiceReplyMixKind,
})
export type UpdateVoiceReplyMixKindRequest = z.infer<typeof UpdateVoiceReplyMixKindRequest>
